'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter, useParams } from 'next/navigation';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faPencil,
  faTrash,
  faArrowRightArrowLeft,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { DashboardToolbar } from '../DashboardToolbar';
import {
  InternalViewBadge,
  PageHeader,
} from 'src/components/Settings/components';
import { NewActionButtons } from '../ActionButtons';
import { useDashboard } from 'src/context/providers/DashboardProvider';
import { useAuthorization } from 'src/hooks/authorization';
import {
  PulseButton,
  PulseIconButton,
  Checkbox,
  CircleDivider,
  CircularProgress,
  EmptyState,
  Tooltip,
  Unauthorized,
} from 'src/components';
import { useSettings } from 'src/components/Settings/hooks';
import { BulkRoleAddDrawer } from '../BulkRoleAddDrawer';
import { ConvertDemoDialog } from '../ConvertDemoDialog';
import { SeatLimitDialog } from '../SeatLimitDialog';
import {
  useGetCompanyByIdQuery,
  useGetCompanyUserQuery,
  useGetCompanyUsersQuery,
} from 'src/api/redux';
import {
  AccessTags,
  ColumnHeader,
  LicenseOverview,
  UserStatusDot,
} from './components';
import {
  COLUMN_LABELS,
  COLUMN_TOOLTIPS,
  DEFAULT_STATUS_FILTER,
  EMPTY_STATE_COPY,
  ROWS_PER_BATCH,
  SCROLL_THRESHOLD_PX,
  USERS_REQUEST_PAGE_SIZE,
} from './constants';
import {
  filterUsersBySearch,
  filterUsersByStatus,
  formatLastActive,
  formatLastActiveDetail,
  getAccessTags,
  getLicenseSummary,
  getStatusLegend,
  getUserStatus,
} from './utils';
import type { UserStatusFilter, UserTableSort } from './types';

/**
 * @description UserTable component displays a company's users alongside its
 *              license allocation, with columns and actions rendered according
 *              to the viewer's user-management capabilities.
 * @returns React.ReactElement
 */
const UserTable: React.FC = (): React.ReactElement => {
  const router = useRouter();
  const { companyId } = useParams<{ companyId: string }>();
  const { userManagementCapabilities } = useAuthorization();
  const {
    canView,
    canWrite,
    canDeactivate,
    canExport,
    isStaff,
    canImpersonate,
  } = userManagementCapabilities;
  const { selectedUsers, setSelectedUsers } = useSettings();

  // Track hovered user ID as string to match the type from user.id.toString()
  const [hoveredUserId, setHoveredUserId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>(
    DEFAULT_STATUS_FILTER
  );
  const [sort, setSort] = useState<UserTableSort>({
    sort: 'last_name',
    order: 'asc',
  });
  // Rows are revealed in batches as the viewer scrolls rather than paged.
  const [visibleCount, setVisibleCount] = useState(ROWS_PER_BATCH);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [showConvertDemoDialog, setShowConvertDemoDialog] = useState(false);
  // Opens in place of the add-user dialog when the account is out of licenses.
  const [showSeatLimitDialog, setShowSeatLimitDialog] = useState(false);

  const { setShowDeactivateCompanyModal, setShowAddEditCompanyModal } =
    useDashboard();

  const { data: company } = useGetCompanyByIdQuery(
    {
      companyId: companyId,
    },
    {
      skip: !companyId,
    }
  );

  const { data: supportUser } = useGetCompanyUserQuery(
    {
      companyId: companyId,
      userId: company?.support_user_id,
    },
    {
      skip: !companyId || !company?.support_user_id,
    }
  );

  // Sorting stays server-side, but the whole company is fetched in one page and
  // searched/filtered locally. "Invited" is derived per row from `last_seen_at`
  // (the API has no invite state), so the status filter is only accurate with
  // every row in hand — and search is instant as a side effect. The license
  // panel's own counts come from the company payload, not from these rows.
  const { data: users, isLoading } = useGetCompanyUsersQuery(
    {
      companyId: companyId,
      ...sort,
      page: 1,
      pageSize: USERS_REQUEST_PAGE_SIZE,
      active_only: 0,
    },
    {
      skip: !companyId,
    }
  );

  const loadedUsers: MedScout.User[] = users?.results ?? [];
  const companyUserCount = users?.count ?? 0;
  // Surfaced rather than swallowed — see USERS_REQUEST_PAGE_SIZE.
  const isTruncated = companyUserCount > loadedUsers.length;

  // Seat figures for the license dialog, from the same source the overview uses.
  const { usedLicenses, totalLicenses } = getLicenseSummary(company);

  const filteredUsers = useMemo(
    () =>
      filterUsersBySearch(
        filterUsersByStatus(loadedUsers, statusFilter),
        searchTerm
      ),
    [loadedUsers, statusFilter, searchTerm]
  );

  const visibleUsers = useMemo(
    () => filteredUsers.slice(0, visibleCount),
    [filteredUsers, visibleCount]
  );

  // Whether any loaded row reads as Active, derived the same way the table
  // filters — not from the company payload's count, whose meaning shifts on API
  // builds predating MKT-445 (see `getLicenseSummary`).
  const hasActiveUsers = useMemo(
    () => filterUsersByStatus(loadedUsers, DEFAULT_STATUS_FILTER).length > 0,
    [loadedUsers]
  );

  const hasMoreToReveal = visibleCount < filteredUsers.length;

  // Reveal the next batch as the viewer nears the bottom of the scroll area.
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasMoreToReveal) return;

      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

      if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD_PX) {
        setVisibleCount((previous) => previous + ROWS_PER_BATCH);
      }
    },
    [hasMoreToReveal]
  );

  // Keep track of all selected users, including rows scrolled past
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    new Set()
  );

  // Key the sync below on the selection's *contents*, not the array identity.
  // The effect always stores a fresh Set, so if `selectedUsers` ever arrives
  // with a new identity on every render the two feed each other until React
  // bails out with "Maximum update depth exceeded".
  const selectedUsersKey = useMemo(
    () =>
      (selectedUsers ?? [])
        .map((selected) => selected.id.toString())
        .sort()
        .join(','),
    [selectedUsers]
  );

  // Mirror the shared selection into local ids (empty key clears it).
  useEffect(() => {
    setSelectedUserIds(
      new Set(selectedUsersKey ? selectedUsersKey.split(',') : [])
    );
  }, [selectedUsersKey]);

  // Start again from the first batch whenever the result set changes underneath
  // the viewer, and scroll them back to the top of the new list.
  useEffect(() => {
    setVisibleCount(ROWS_PER_BATCH);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [searchTerm, statusFilter, sort]);

  // Failsafe (MKT-494): the table opens on Active, which strands the viewer on
  // an empty table when nobody has signed in yet — an account of nothing but
  // invited or deactivated users. Once rows are in hand, fall back to showing
  // everyone, moving the toolbar's own filter so it is clear why.
  const hasCheckedActiveFallbackRef = useRef(false);

  // A company switch is a fresh account to check, and the component can outlive
  // one.
  useEffect(() => {
    hasCheckedActiveFallbackRef.current = false;
  }, [companyId]);

  useEffect(() => {
    if (hasCheckedActiveFallbackRef.current || isLoading) return;
    // Nothing loaded yet — leave the check unspent so it still runs once rows
    // arrive. An account with genuinely no users has nothing to fall back to.
    if (!loadedUsers.length) return;

    // Spent whatever the outcome: after this the filter is the viewer's to set,
    // so deliberately choosing Active on such an account keeps the empty state
    // and its Clear filters action rather than being overridden.
    hasCheckedActiveFallbackRef.current = true;

    if (statusFilter !== DEFAULT_STATUS_FILTER || hasActiveUsers) return;

    setStatusFilter('all');
  }, [hasActiveUsers, isLoading, loadedUsers.length, statusFilter]);

  // Handle clearing all selections
  const handleClearSelection = () => {
    setSelectedUserIds(new Set());
    setSelectedUsers([]);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const handleColSort = (field: string) => {
    setSort((previous) =>
      previous.sort === field
        ? { sort: field, order: previous.order === 'asc' ? 'desc' : 'asc' }
        : { sort: field, order: 'asc' }
    );
  };

  const handleRowClick = (user: MedScout.User) => {
    setSearchTerm('');
    router.push(`/settings/company/${companyId}/users/${user.id}`);
  };

  const handleDeactivateCompany = () => {
    setShowDeactivateCompanyModal(true);
  };

  const handleShowAddEditCompanyModal = () => {
    setShowAddEditCompanyModal(true);
  };

  const handleSelectAll = () => {
    if (!visibleUsers.length) return;

    const visibleUserIds = visibleUsers.map((user) => user.id.toString());
    const newSelectedIds = new Set(selectedUserIds);

    const allCurrentSelected = visibleUserIds.every((id) =>
      selectedUserIds.has(id)
    );

    if (allCurrentSelected) {
      // Deselect every revealed user
      visibleUserIds.forEach((id) => newSelectedIds.delete(id));
      // Update selectedUsers by removing the revealed users
      setSelectedUsers(
        selectedUsers.filter(
          (user) => !visibleUserIds.includes(user.id.toString())
        )
      );
    } else {
      // Select every revealed user
      visibleUserIds.forEach((id) => newSelectedIds.add(id));
      // Update selectedUsers by adding the revealed users
      const newUsers = visibleUsers.filter(
        (user) => !selectedUsers.find((u) => u.id === user.id)
      );
      setSelectedUsers([...selectedUsers, ...newUsers]);
    }

    setSelectedUserIds(newSelectedIds);
  };

  const handleSelectUser = (user: MedScout.User) => {
    const userId = user.id.toString();
    const newSelectedIds = new Set(selectedUserIds);

    if (newSelectedIds.has(userId)) {
      newSelectedIds.delete(userId);
      setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
    } else {
      newSelectedIds.add(userId);
      setSelectedUsers([...selectedUsers, user]);
    }

    setSelectedUserIds(newSelectedIds);
  };

  // If user doesn't have permission, show unauthorized message
  if (!canView) {
    return (
      <Unauthorized
        title="Access Restricted"
        message="You don't have permission to view the user table. If you believe you should have access, please contact support."
        primaryActionText="Go to Settings"
        onPrimaryAction={() => router.push('/settings')}
      />
    );
  }

  // Check if every currently-revealed user is selected
  const areAllVisibleSelected =
    visibleUsers.length > 0 &&
    visibleUsers.every((user) => selectedUserIds.has(user.id.toString()));

  // Selection + Status, Name, Access, Manager, Last Active, Actions
  const columnCount = 6 + (canWrite ? 1 : 0);
  const isEmpty = !isLoading && visibleUsers.length === 0;

  return (
    // Right padding on wide viewports keeps the table from stretching the full
    // width of a large monitor.
    <div className="relative flex flex-col gap-6 pb-6 lg:pr-12 xl:pr-20">
      <div className="flex-none">
        <PageHeader>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-lg font-semibold text-neutral-900">
                  {company?.name}
                </h2>
                {isStaff && (
                  <PulseIconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    icon={faPencil}
                    onClick={handleShowAddEditCompanyModal}
                    aria-label="Edit company"
                    tooltip="Edit company"
                    data-testid="edit-company-button"
                  />
                )}
                {isStaff && <InternalViewBadge />}
              </div>
              {/* Internal ids + support user are MedScout-only, and sit on one
                  divider-separated line under the name (mirroring
                  DynamicProfileHeader's meta row) rather than stacking. */}
              {isStaff && (
                <div
                  className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-neutral-500"
                  data-testid="internal-ids"
                >
                  <span className="shrink-0">Company id: {company?.id}</span>
                  <CircleDivider />
                  <span className="truncate">
                    Support User: {supportUser?.username}
                  </span>
                </div>
              )}
              {/* The HubSpot link (MKT-669) gets its own line, so the HubSpot
                  id can't be read as part of the MedScout ids above. */}
              {isStaff && (
                <div
                  className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-neutral-500"
                  data-testid="hubspot-info"
                >
                  {company?.hubspot_company_id ? (
                    <>
                      <span className="truncate">
                        HubSpot domain: {company?.hubspot_domain || 'None'}
                      </span>
                      <CircleDivider />
                      <span className="shrink-0">
                        HubSpot ID: {company.hubspot_company_id}
                      </span>
                    </>
                  ) : (
                    // Loud on purpose: every company should be linked to its
                    // HubSpot record as soon as possible (MKT-669).
                    <>
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-red-700 bg-red-100 px-2 py-1 text-xs font-semibold text-red-700"
                        data-testid="hubspot-not-connected-badge"
                      >
                        <Icon
                          icon={faTriangleExclamation}
                          aria-hidden="true"
                        />
                        Not connected to HubSpot
                      </span>
                      <PulseButton
                        size="sm"
                        variant="solid"
                        color="danger"
                        onClick={handleShowAddEditCompanyModal}
                        data-testid="hubspot-connect-button"
                      >
                        Connect now
                      </PulseButton>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {isStaff && company?.is_demo && (
                <PulseButton
                  size="sm"
                  variant="solid"
                  color="primary"
                  onClick={() => setShowConvertDemoDialog(true)}
                  startDecorator={faArrowRightArrowLeft}
                  data-testid="convert-demo-button"
                  aria-label={`Convert ${
                    company?.name || 'company'
                  } from demo to customer`}
                >
                  Convert to Customer
                </PulseButton>
              )}
              {isStaff && (
                <PulseButton
                  size="sm"
                  variant="outline"
                  color="danger"
                  onClick={handleDeactivateCompany}
                  disabled={company?.name?.includes('MedScout')}
                  startDecorator={faTrash}
                  data-testid="deactivate-company-button"
                  aria-label={`Deactivate ${company?.name || 'company'}`}
                >
                  Deactivate
                </PulseButton>
              )}
            </div>
          </div>
        </PageHeader>
      </div>

      <LicenseOverview company={company} />

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 h-full flex flex-col">
        {/* Toolbar */}
        <div className="px-4 pt-4 pb-4">
          <DashboardToolbar
            search={searchTerm}
            onSearch={handleSearch}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            resultCount={filteredUsers.length}
            loadedCount={isTruncated ? loadedUsers.length : undefined}
            showUserControls
            setSelectedUsers={handleClearSelection}
            canWrite={canWrite}
            canExport={canExport}
            canImpersonate={canImpersonate}
            onSeatLimitReached={() => setShowSeatLimitDialog(true)}
          />
        </div>

        {/* Table Container */}
        {/* Single scroller: the inner div below owns scrolling so the row
            footer stays put and infinite scroll has one source of truth. */}
        <div
          className="relative border border-neutral-200 overflow-hidden
          flex-1 flex flex-col max-h-[calc(100vh-28rem)]"
        >
          {/* Blur loading overlay over table area */}
          {isLoading && (
            <div
              className="absolute inset-0 bg-white/50 backdrop-blur-sm z-10 flex items-center justify-center"
              role="status"
              aria-label="Loading users"
              data-testid="user-table-loading"
            >
              <div className="flex flex-col items-center gap-3">
                <CircularProgress size="sm" color="neutral" />
                <span className="text-sm text-neutral-600 font-medium">
                  Loading users...
                </span>
              </div>
            </div>
          )}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="overflow-auto flex-1"
            data-testid="user-table-scroll-container"
          >
            <table
              className="w-full divide-y divide-neutral-200"
              aria-label="Company users"
              data-testid="user-table"
            >
              <thead className="bg-neutral-50 sticky top-0">
                <tr>
                  {/* Selection exists only to feed bulk role add */}
                  {canWrite && (
                    <th
                      scope="col"
                      className="w-8 px-2 py-3 text-left text-xs font-semibold text-neutral-700 bg-neutral-50 whitespace-nowrap"
                    >
                      <Checkbox
                        size="sm"
                        checked={areAllVisibleSelected}
                        onChange={handleSelectAll}
                        className="text-neutral-500"
                        disabled={visibleUsers.length === 0}
                        aria-label="Select all loaded users"
                      />
                    </th>
                  )}
                  <ColumnHeader
                    label={COLUMN_LABELS.status}
                    tooltip={
                      <span
                        className="flex flex-col gap-1"
                        data-testid="status-legend"
                      >
                        {getStatusLegend().map((line) => (
                          <span key={line}>{line}</span>
                        ))}
                      </span>
                    }
                    className="w-12 text-center"
                    data-testid="column-status"
                  />
                  <ColumnHeader
                    label={COLUMN_LABELS.name}
                    tooltip={COLUMN_TOOLTIPS.name}
                    sortField="last_name"
                    activeSort={sort}
                    onSort={handleColSort}
                    className="w-56"
                    data-testid="column-name"
                  />
                  <ColumnHeader
                    label={COLUMN_LABELS.access}
                    tooltip={COLUMN_TOOLTIPS.access}
                    className="min-w-40"
                    data-testid="column-access"
                  />
                  <ColumnHeader
                    label={COLUMN_LABELS.manager}
                    tooltip={COLUMN_TOOLTIPS.manager}
                    className="w-32"
                    data-testid="column-manager"
                  />
                  {/* The one column whose figure is easy to misread, so it
                      advertises its caveat with an icon (MKT-526, MKT-557).
                      `last_seen_at` lives on the profile, so the sort key has to
                      be the traversal: the API validates only the first segment
                      of the path and silently drops a sort it doesn't recognise,
                      which would leave the header looking sortable but inert. */}
                  <ColumnHeader
                    label={COLUMN_LABELS.lastActive}
                    tooltip={COLUMN_TOOLTIPS.lastActive}
                    showTooltipIcon
                    sortField="profile__last_seen_at"
                    activeSort={sort}
                    onSort={handleColSort}
                    className="w-32"
                    data-testid="column-last-active"
                  />
                  <th
                    scope="col"
                    className="w-16 px-2 py-3 text-right text-xs font-semibold text-neutral-700 bg-neutral-50 whitespace-nowrap sticky right-0 shadow-[-4px_0_8px_-6px_rgba(0,0,0,0.1)] z-20"
                  >
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {isEmpty && (
                  <tr>
                    <td colSpan={columnCount} className="p-4">
                      <EmptyState
                        title={EMPTY_STATE_COPY.title}
                        description={EMPTY_STATE_COPY.description}
                        className="border-0 bg-transparent shadow-none"
                        action={
                          <PulseButton
                            size="sm"
                            variant="outline"
                            color="neutral"
                            onClick={handleClearFilters}
                            className="rounded-full"
                            aria-label={EMPTY_STATE_COPY.action}
                            data-testid="clear-filters-button"
                          >
                            {EMPTY_STATE_COPY.action}
                          </PulseButton>
                        }
                      />
                    </td>
                  </tr>
                )}
                {visibleUsers.map((user: MedScout.User) => (
                  <tr
                    key={user.id}
                    onClick={() => handleRowClick(user)}
                    onMouseEnter={() => setHoveredUserId(user.id.toString())}
                    onMouseLeave={() => setHoveredUserId(null)}
                    className="group hover:bg-neutral-50 cursor-pointer transition-colors duration-150"
                  >
                    {canWrite && (
                      <td className="w-8 px-2 py-3 whitespace-nowrap text-xs text-neutral-900">
                        <Checkbox
                          size="sm"
                          checked={selectedUserIds.has(user.id.toString())}
                          onChange={() => handleSelectUser(user)}
                          className="text-neutral-500"
                          aria-label={`Select ${user.first_name} ${user.last_name}`}
                        />
                      </td>
                    )}
                    <td className="w-12 px-2 py-3 text-center whitespace-nowrap text-xs text-neutral-900">
                      <UserStatusDot status={getUserStatus(user)} />
                    </td>
                    <td className="w-56 max-w-56 px-2 py-3 text-xs text-neutral-900">
                      <div className="flex flex-col">
                        <span className="truncate font-medium">
                          {`${user.first_name ?? ''} ${
                            user.last_name ?? ''
                          }`.trim() || user.username}
                        </span>
                        <span
                          className="truncate text-neutral-600"
                          title={user.username}
                        >
                          {user.username}
                        </span>
                      </div>
                    </td>
                    <td className="min-w-40 px-2 py-3 text-xs text-neutral-900">
                      <AccessTags tags={getAccessTags(user)} />
                    </td>
                    <td className="w-32 px-2 py-3 whitespace-nowrap text-xs text-neutral-900">
                      {user.managers?.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {user.managers.map((manager, idx) => (
                            <span
                              key={idx}
                              className="truncate text-xs"
                              title={`${manager.first_name} ${manager.last_name}`}
                            >
                              {`${manager.first_name} ${manager.last_name}`}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-neutral-500">—</span>
                      )}
                    </td>
                    <td className="w-32 px-2 py-3 whitespace-nowrap text-xs text-neutral-900">
                      <Tooltip
                        content={formatLastActiveDetail(user.last_seen_at)}
                        delay={200}
                      >
                        <span className="truncate">
                          {formatLastActive(user.last_seen_at)}
                        </span>
                      </Tooltip>
                    </td>
                    {/* The sticky cell needs its own opaque background to cover
                        the scrolling columns, so it follows the row's hover via
                        `group-hover` — a plain `hover:` here only fired when the
                        cursor was over this cell, leaving the row highlight
                        stopping short on the right. */}
                    <td className="w-16 px-2 py-3 whitespace-nowrap text-xs text-neutral-900 sticky right-0 bg-white group-hover:bg-neutral-50 transition-colors duration-150 shadow-[-4px_0_8px_-6px_rgba(0,0,0,0.1)]">
                      <div className="flex justify-end">
                        <NewActionButtons
                          showImpersonate={canImpersonate}
                          canWrite={canWrite}
                          canDeactivate={canDeactivate}
                          selectedUser={user}
                          isHovered={hoveredUserId === user.id.toString()}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {canWrite && <BulkRoleAddDrawer />}
      <ConvertDemoDialog
        open={showConvertDemoDialog}
        onClose={() => setShowConvertDemoDialog(false)}
      />
      <SeatLimitDialog
        open={showSeatLimitDialog}
        onClose={() => setShowSeatLimitDialog(false)}
        usedLicenses={usedLicenses}
        totalLicenses={totalLicenses}
      />
    </div>
  );
};

export default UserTable;
